import { createApi } from '@reduxjs/toolkit/query/react';

import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import type {
  ListMortgagesParams,
  ListMortgagesResponse,
} from '@/pages/property-details/types/mortgage.types';
import type {
  ListPropertiesParams,
  ListPropertiesResponse,
  PropertyOwner,
} from '@/pages/property-details/types/property.types';

/**
 * Properties domain API.
 *
 * Backend: `PropertiesController` (`/v1/properties/...`). The list endpoint
 * returns `ApiResponse<ListPropertiesDto>`; we unwrap `.data` via
 * `transformResponse` so callers read the paginated envelope directly.
 */
export const propertyDetailApi = createApi({
  reducerPath: 'propertyDetailApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Property'],
  endpoints: (builder) => ({
    listProperties: builder.query<ListPropertiesResponse, ListPropertiesParams>(
      {
        query: ({
          page = 1,
          limit = 20,
          searchParam,
          clientSearch,
          purchasePriceMin,
          purchasePriceMax,
          purchaseDateFrom,
          purchaseDateTo,
          rentalDateFrom,
          rentalDateTo,
          location,
          linkedToHubspot,
        } = {}) => {
          const params = new URLSearchParams();
          params.set('page', String(page));
          params.set('limit', String(limit));
          if (searchParam) params.set('searchParam', searchParam);
          if (clientSearch) params.set('clientSearch', clientSearch);
          if (purchasePriceMin != null)
            params.set('purchasePriceMin', String(purchasePriceMin));
          if (purchasePriceMax != null)
            params.set('purchasePriceMax', String(purchasePriceMax));
          if (purchaseDateFrom)
            params.set('purchaseDateFrom', purchaseDateFrom);
          if (purchaseDateTo) params.set('purchaseDateTo', purchaseDateTo);
          if (rentalDateFrom) params.set('rentalDateFrom', rentalDateFrom);
          if (rentalDateTo) params.set('rentalDateTo', rentalDateTo);
          if (location) params.set('location', location);
          if (linkedToHubspot != null)
            params.set('linkedToHubspot', String(linkedToHubspot));
          return `/v1/properties/list?${params.toString()}`;
        },
        transformResponse: (response: ApiResponse<ListPropertiesResponse>) =>
          response.data,
        providesTags: ['Property'],
      },
    ),

    listMortgages: builder.query<ListMortgagesResponse, ListMortgagesParams>({
      query: ({
        page = 1,
        limit = 20,
        lenderSearch,
        clientSearch,
        mortgageBalanceMin,
        mortgageBalanceMax,
        interestRateMin,
        interestRateMax,
        location,
        source,
        linkedToHubspot,
      } = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (lenderSearch) params.set('lenderSearch', lenderSearch);
        if (clientSearch) params.set('clientSearch', clientSearch);
        if (mortgageBalanceMin != null)
          params.set('mortgageBalanceMin', String(mortgageBalanceMin));
        if (mortgageBalanceMax != null)
          params.set('mortgageBalanceMax', String(mortgageBalanceMax));
        if (interestRateMin != null)
          params.set('interestRateMin', String(interestRateMin));
        if (interestRateMax != null)
          params.set('interestRateMax', String(interestRateMax));
        if (location) params.set('location', location);
        if (source) params.set('source', source);
        if (linkedToHubspot != null)
          params.set('linkedToHubspot', String(linkedToHubspot));
        return `/v1/properties/mortgages/list?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<ListMortgagesResponse>) =>
        response.data,
      providesTags: ['Property'],
    }),

    /**
     * Owners (linked clients) for a single property — id, name, email and
     * ownership share. Lazily fetched when a list row is expanded.
     */
    getPropertyOwners: builder.query<PropertyOwner[], string>({
      query: (propertyId) => `/v1/properties/${propertyId}/owners`,
      transformResponse: (response: ApiResponse<PropertyOwner[]>) =>
        response.data,
    }),
  }),
});

export const {
  useListPropertiesQuery,
  useListMortgagesQuery,
  useGetPropertyOwnersQuery,
} = propertyDetailApi;
